<script setup lang="ts">
  import draggable from 'vuedraggable'
  import type { CartItem } from '@/types/Cart'

  const props = defineProps<{ modelValue: CartItem[] }>()
  const emit = defineEmits<{
    'toggle-complete': [cartItem: CartItem]
    'remove-item': [cartItem: CartItem]
    'update:modelValue': [items: CartItem[]]
  }>()

  const value = computed({
    get: () => props.modelValue,
    set: (value) => emit('update:modelValue', value),
  })
</script>

<template>
  <draggable
    v-model="value"
    group="cart"
    item-key="id"
    class="cart__itemList"
    ghost-class="cart__item--ghost"
    :delay="200"
  >
    <template #item="{ element }">
      <div class="cart__item" :class="`cart__item--${element.complete ? 'complete' : 'incomplete'}`">
        <label class="cart__itemLabel">
          <Checkbox :checked="element.complete" @change="emit('toggle-complete', element)" />
          <span>{{ element.name }}</span>
          <div class="cart__itemActions">
            <NuxtLink v-if="element.recipeSlug" :to="`/recipe/${element.recipeSlug}`" prefetch class="cart__itemLink">
              <i class="fad fa-fw fa-pot-food"></i>
            </NuxtLink>
            <button class="cart__itemRemove" @click.stop.prevent="emit('remove-item', element)">
              <i class="far fa-times"></i>
            </button>
          </div>
        </label>
      </div>
    </template>
  </draggable>
</template>

<style lang="sass">
  .cart
    &__itemList
      list-style: none
      padding: 0
      width: min-content
      min-width: 100%

    &__item
      @apply text-lg
      display: flex
      align-items: center
      position: relative
      gap: .5em
      margin-bottom: .35rem
      line-height: 1.3
      max-width: var(--page-max-width)

      &--complete
        opacity: .5
        text-decoration: line-through

      &--ghost
        opacity: .5
        font-style: italic
        background-color: rgb(0 0 0 / .05)

      &Label
        display: flex
        align-items: flex-start
        width: 100%
        gap: .5em
        padding: .5rem 0
        > .checkbox__wrapper
          margin-top: 2px
        > span
          margin-right: 1rem

      &Actions
        margin-left: auto
        display: flex
        align-items: center

      &Link
        margin-right: .5rem
        color: theme('colors.slate.600')
        background-color: theme('colors.slate.200')
        font-size: 0.75rem
        padding: 0.25rem
        border-radius: 4px
        transition: background-color .2s ease-in-out
        &:hover
          background-color: theme('colors.slate.300')

      &Remove
        color: theme('colors.slate.400')
        transition: color .2s ease-in-out
        &:hover
          color: theme('colors.slate.600')
</style>
